import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PreviewIcon from '@mui/icons-material/Preview';
import PublishIcon from '@mui/icons-material/Publish';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  createDailyPageActivity,
  deleteDailyPageActivity,
  getDailyPageDay,
  getDailyPageDayConfig,
  getDailyPageDayPreview,
  getDailyPageErrorMessage,
  getDailyPagePayload,
  isDailyPageApiSuccess,
  publishDailyPageDay,
  putDailyPageDayConfig,
  unpublishDailyPageDay,
  updateDailyPageActivity,
} from '../../services/dailyPageApi';
import {
  getDailyMissionsPayload,
  isDailyMissionsApiSuccess,
  listDailyMissionActions,
  listDailyMissions,
} from '../../services/dailyMissionsApi';
import { parseActionOptions, parseDayList } from '../DailyMissions/dailyMissionUtils';
import DayRewardsForm from './DayRewardsForm';
import MissionsGroupPanel from './MissionsGroupPanel';
import ActivitiesGroupPanel from './ActivitiesGroupPanel';
import DayPreviewDrawer from './DayPreviewDrawer';
import {
  CHALLENGE_TYPES,
  DEFAULT_DAY_CONFIG,
  OPINION_TYPES,
  dayMaxFe,
  filterActivities,
  formatIstDate,
  isDayKey,
  parseDayConfig,
  parsePublishChecklist,
  publishStatus,
} from './dailyTasksUtils';

function statusChip(status) {
  if (status === 'locked') return { label: 'Locked', color: 'warning' };
  if (status === 'published') return { label: 'Published', color: 'success' };
  return { label: 'Draft', color: 'default' };
}

function DailyTasksDayPage() {
  const { date } = useParams();
  const validDate = isDayKey(date);

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [day, setDay] = useState(null);
  const [mission, setMission] = useState(null);
  const [activities, setActivities] = useState([]);
  const [dayConfig, setDayConfig] = useState({ ...DEFAULT_DAY_CONFIG });
  const [dayConfigSaved, setDayConfigSaved] = useState(false);
  const [actionOptions, setActionOptions] = useState([]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const [publishOpen, setPublishOpen] = useState(false);
  const [allowEmpty, setAllowEmpty] = useState(false);
  const [confirmIst, setConfirmIst] = useState(false);

  const load = useCallback(async () => {
    if (!validDate) return;
    setLoading(true);
    setError('');
    try {
      const [dayRes, configRes, missionsRes, actionsRes] = await Promise.all([
        getDailyPageDay(date),
        getDailyPageDayConfig(date),
        listDailyMissions({ startDate: date, endDate: date, limit: 5 }),
        listDailyMissionActions(),
      ]);

      if (!isDailyPageApiSuccess(dayRes)) {
        // Day may not exist yet — treat as empty draft
        if (dayRes?.http_status === 404) {
          setDay({ activity_date: date, is_published: false, locked: false, activities: [] });
          setActivities([]);
          setMission(null);
        } else {
          setError(getDailyPageErrorMessage(dayRes, 'Failed to load day.'));
        }
      } else {
        const payload = getDailyPagePayload(dayRes) || {};
        setDay(payload);
        setActivities(Array.isArray(payload.activities) ? payload.activities.filter((a) => a.is_active !== false) : []);
        setMission(payload.mission || null);
      }

      if (isDailyPageApiSuccess(configRes)) {
        setDayConfig(parseDayConfig(getDailyPagePayload(configRes)));
        setDayConfigSaved(true);
      } else {
        setDayConfig({ ...DEFAULT_DAY_CONFIG, day_key: date });
        setDayConfigSaved(false);
      }

      if (isDailyMissionsApiSuccess(missionsRes)) {
        const parsed = parseDayList(getDailyMissionsPayload(missionsRes));
        const pack = parsed.days.find((d) => d.mission_date === date) || null;
        if (pack) setMission((prev) => prev || pack);
      }

      if (isDailyMissionsApiSuccess(actionsRes)) {
        setActionOptions(parseActionOptions(getDailyMissionsPayload(actionsRes)));
      }
    } catch (err) {
      setError(err.message || 'Failed to load day.');
    } finally {
      setLoading(false);
    }
  }, [date, validDate]);

  useEffect(() => {
    load();
  }, [load]);

  const status = publishStatus({
    is_published: day?.is_published,
    locked: day?.locked,
  });
  const chip = statusChip(status);
  const available = dayMaxFe(dayConfig);
  const locked = !!day?.locked;

  const challenges = useMemo(
    () => filterActivities(activities, CHALLENGE_TYPES),
    [activities]
  );
  const opinions = useMemo(() => filterActivities(activities, OPINION_TYPES), [activities]);

  const checklist = useMemo(
    () =>
      parsePublishChecklist({
        dayConfigSaved,
        mission,
        activities,
        allowEmpty,
      }),
    [dayConfigSaved, mission, activities, allowEmpty]
  );

  const checklistReady = checklist.every((item) => item.ok) && confirmIst;

  const handleSaveRewards = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await putDailyPageDayConfig(date, {
        missions_group_fe: Number(dayConfig.missions_group_fe) || 0,
        challenge_group_fe: Number(dayConfig.challenge_group_fe) || 0,
        opinions_group_fe: Number(dayConfig.opinions_group_fe) || 0,
        completion_bonus_fe: Number(dayConfig.completion_bonus_fe) || 0,
      });
      if (!isDailyPageApiSuccess(res)) {
        setError(getDailyPageErrorMessage(res, 'Failed to save day-config.'));
        return;
      }
      setDayConfigSaved(true);
      setSnackbar({
        open: true,
        message:
          'Rewards saved. Fans who already opened this IST day keep previous amounts; new fans see these.',
        severity: 'success',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateActivity = async (payload) => {
    setSaving(true);
    try {
      const res = await createDailyPageActivity(date, payload);
      if (!isDailyPageApiSuccess(res)) {
        setSnackbar({
          open: true,
          message: getDailyPageErrorMessage(res, 'Failed to create activity.'),
          severity: 'error',
        });
        return;
      }
      setSnackbar({ open: true, message: 'Activity created.', severity: 'success' });
      await load();
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateActivity = async (id, payload) => {
    setSaving(true);
    try {
      const res = await updateDailyPageActivity(id, payload);
      if (!isDailyPageApiSuccess(res)) {
        setSnackbar({
          open: true,
          message: getDailyPageErrorMessage(res, 'Failed to update activity.'),
          severity: 'error',
        });
        return;
      }
      setSnackbar({ open: true, message: 'Activity updated.', severity: 'success' });
      await load();
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id, { forceCorrection } = {}) => {
    setSaving(true);
    try {
      const res = await deleteDailyPageActivity(id, { forceCorrection });
      if (!isDailyPageApiSuccess(res)) {
        setSnackbar({
          open: true,
          message: getDailyPageErrorMessage(res, 'Failed to deactivate activity.'),
          severity: 'error',
        });
        return;
      }
      setSnackbar({ open: true, message: 'Activity deactivated.', severity: 'success' });
      await load();
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const res = await getDailyPageDayPreview(date);
      if (isDailyPageApiSuccess(res)) {
        setPreview(getDailyPagePayload(res));
      } else {
        setPreview(null);
        setSnackbar({
          open: true,
          message: getDailyPageErrorMessage(res, 'Preview failed — showing local stack.'),
          severity: 'warning',
        });
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  const handlePublish = async () => {
    setSaving(true);
    try {
      const res = await publishDailyPageDay(date);
      if (!isDailyPageApiSuccess(res)) {
        setSnackbar({
          open: true,
          message: getDailyPageErrorMessage(res, 'Publish failed.'),
          severity: 'error',
        });
        return;
      }
      setPublishOpen(false);
      setSnackbar({
        open: true,
        message:
          'Published and locked. Mid-day edits do not rewrite fans who already opened this IST day. Force correction never claws back credited FE.',
        severity: 'success',
      });
      await load();
    } finally {
      setSaving(false);
    }
  };

  const handleUnpublish = async () => {
    setSaving(true);
    try {
      const res = await unpublishDailyPageDay(date);
      if (!isDailyPageApiSuccess(res)) {
        setSnackbar({
          open: true,
          message: getDailyPageErrorMessage(res, 'Unpublish failed.'),
          severity: 'error',
        });
        return;
      }
      setSnackbar({
        open: true,
        message: 'Unpublished. Extras are hidden; progress is not deleted.',
        severity: 'success',
      });
      await load();
    } finally {
      setSaving(false);
    }
  };

  if (!validDate) {
    return (
      <Alert severity="error">
        Invalid date. Use an IST calendar day as <code>YYYY-MM-DD</code>.{' '}
        <Link to="/admin/daily-tasks">Back to calendar</Link>
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          py: 1.5,
          mb: 2,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton component={Link} to="/admin/daily-tasks">
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h6" sx={{ m: 0 }}>
                {formatIstDate(date)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                IST calendar day · {date}
              </Typography>
            </Box>
            <Chip size="small" label={chip.label} color={chip.color} />
            <Chip size="small" color="primary" variant="outlined" label={`Available today ${available} FE`} />
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button size="small" startIcon={<PreviewIcon />} onClick={openPreview}>
              Preview
            </Button>
            {day?.is_published ? (
              <Button
                size="small"
                color="warning"
                startIcon={<UnpublishedIcon />}
                onClick={handleUnpublish}
                disabled={saving}
              >
                Unpublish
              </Button>
            ) : (
              <Button
                size="small"
                variant="contained"
                startIcon={<PublishIcon />}
                onClick={() => {
                  setAllowEmpty(false);
                  setConfirmIst(false);
                  setPublishOpen(true);
                }}
                disabled={saving}
              >
                Publish
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {locked && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This day is locked. Edits require force correction and are audited. Force correction never
          claws back FE already credited to fans.
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Overview & rewards" />
        <Tab label={`Missions${mission?.tasks?.length ? ` (${mission.tasks.length})` : ''}`} />
        <Tab label={`Challenge (${challenges.length})`} />
        <Tab label={`Opinions (${opinions.length})`} />
      </Tabs>

      {tab === 0 && (
        <DayRewardsForm
          form={dayConfig}
          onChange={setDayConfig}
          onSave={handleSaveRewards}
          saving={saving}
        />
      )}
      {tab === 1 && <MissionsGroupPanel date={date} mission={mission} />}
      {tab === 2 && (
        <ActivitiesGroupPanel
          group="challenge"
          activities={challenges}
          actionOptions={actionOptions}
          locked={locked}
          onCreate={handleCreateActivity}
          onUpdate={handleUpdateActivity}
          onDeactivate={handleDeactivate}
          saving={saving}
        />
      )}
      {tab === 3 && (
        <ActivitiesGroupPanel
          group="opinions"
          activities={opinions}
          actionOptions={actionOptions}
          locked={locked}
          onCreate={handleCreateActivity}
          onUpdate={handleUpdateActivity}
          onDeactivate={handleDeactivate}
          saving={saving}
        />
      )}

      <DayPreviewDrawer
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        loading={previewLoading}
        preview={preview}
        dayConfig={dayConfig}
        mission={mission}
        activities={activities}
      />

      <Dialog open={publishOpen} onClose={() => !saving && setPublishOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Publish checklist</DialogTitle>
        <DialogContent>
          <List dense>
            {checklist.map((item) => (
              <ListItem key={item.id}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Checkbox edge="start" checked={item.ok} disabled />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  secondary={item.detail}
                  primaryTypographyProps={{ color: item.ok ? 'text.primary' : 'error' }}
                />
              </ListItem>
            ))}
          </List>
          <FormControlLabel
            control={
              <Checkbox checked={allowEmpty} onChange={(e) => setAllowEmpty(e.target.checked)} />
            }
            label="Consciously empty day (no groups required)"
          />
          <FormControlLabel
            control={
              <Checkbox checked={confirmIst} onChange={(e) => setConfirmIst(e.target.checked)} />
            }
            label={`Confirm IST date is correct: ${formatIstDate(date)}`}
          />
          <Alert severity="warning" sx={{ mt: 1 }}>
            Publishing locks the day. Snapshot rule: fans who already opened keep shown amounts.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePublish}
            disabled={!checklistReady || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <PublishIcon />}
          >
            Publish
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DailyTasksDayPage;
